import Tippy from '@tippyjs/react/headless';
import { Button, Popconfirm, message } from "antd";
import { memo, useCallback, useRef } from "react";
import { useAtom, useSetAtom } from 'jotai';
import styled from "styled-components";
import { MapData_Base, MapData_ProcessStatus } from '~/common/typescript/models/MapData';
import {
  selectedMapDataAtom,
  showDataTableAtom,
  showStylePopupAtom,
  showMapDataDetailPanelAtom,
  mapDataListAtom,
  mapDataListRefreshTrigger,
} from "~/components/Map/stores/mapStore";
import MapUtils from '~/components/Map/utils/MapUtils';
import MapDataUtils from '~/components/Map/utils/MapDataUtils';
import { BallTriangle, Oval, RotatingTriangles, ThreeCircles } from 'react-loader-spinner';
import { PiTable, PiCopy, PiTrash, PiEye, PiEyeSlash, PiInfo } from 'react-icons/pi';
import { TbFileExport } from "react-icons/tb";
import { LuFilePen } from "react-icons/lu";
import { IoEllipsisHorizontal } from "react-icons/io5";

import { MdClose } from 'react-icons/md';
import { ShareMapDataProjectAPIs } from '~/common/typescript/apis/ShareMapDataProjectAPIs';
import { currentProjectAtom } from "~/stores/ProjectStore";
import { dataTable_selecetedMapData_Atom } from '../../../DataTablePanel/store';
import { PiMagnifyingGlass } from 'react-icons/pi';
import { LayerItemUtil } from './LayerItemUtil';
import LayerThumbnail from './LayerThumbnail';

interface LayerItemProps {
  data: MapData_Base,
  toggleVisibility: (visible: boolean) => void,
  onDelete?: () => void
}
const LayerItem = (props: LayerItemProps) => {

  const [currentProject, setCurrentProject] = useAtom(currentProjectAtom);
  const setMapDataLayers = useSetAtom(mapDataListAtom);
  const setRefreshTrigger = useSetAtom(mapDataListRefreshTrigger);
  const tippyRef = useRef<any>(null);
  const [selectedMapData, setSelectedMapData] = useAtom(selectedMapDataAtom);
  const [showStylePopup, setShowStylePopup] = useAtom(showStylePopupAtom);
  const [showDataTable, setShowDataTable] = useAtom(showDataTableAtom);
  const [showMapDataDetailPanel, setShowMapDataDetailPanel] = useAtom(showMapDataDetailPanelAtom);
  const [selected_MapData_DataTable, setSelected_MapData_DataTable] = useAtom<MapData_Base>(dataTable_selecetedMapData_Atom);

  const handleFitBounds = useCallback(async () => {
    tippyRef.current.hide();

    await LayerItemUtil.zoomToLayer(props.data);

    if (!props.data.visible) {
      props.toggleVisibility(true);
    }
  }, [])

  const handleEdit = useCallback(async () => {
    tippyRef.current.hide();
    setShowStylePopup(true);
    props.toggleVisibility(true);

    setTimeout(() => {
      setSelectedMapData(props.data)
    }, 200);
  }, [])

  const handleShowDetails = useCallback(() => {
    tippyRef.current.hide();
    setSelectedMapData(props.data);
    setShowMapDataDetailPanel(true);
  }, [])

  if (!props.data) {
    return;
  }

  const onClick_Delete = async () => {
    if (!currentProject?.id || !props.data?.id) return;
    try {
      // 1. Gỡ layer khỏi bản đồ nếu đang có trên bản đồ
      MapDataUtils.removeFromMap(props.data);

      // 2. Cập nhật state UI danh sách ngay lập tức
      setMapDataLayers((prev) => prev.filter((item) => item?.id !== props.data.id));
      props.onDelete?.();

      // 3. Đóng các panel liên quan nếu đang mở layer này
      if (selectedMapData?.id === props.data.id) {
        setSelectedMapData(null);
        setShowMapDataDetailPanel(false);
        setShowStylePopup(false);
      }
      if (selected_MapData_DataTable?.id === props.data.id) {
        setSelected_MapData_DataTable(undefined as any);
        setShowDataTable(false);
      }

      // 4. Gọi API xóa khỏi dự án
      const res = await ShareMapDataProjectAPIs.remote_removeMapDataFromProject(currentProject.id, props.data.id);
      message.success(res?.i18n?.vi || 'Dữ liệu bản đồ đã được xóa khỏi dự án');

      // 5. Kích hoạt refresh trigger để đồng bộ lại dữ liệu
      setRefreshTrigger((prev) => prev + 1);
    } catch (error: any) {
      console.error('Lỗi khi xóa dữ liệu bản đồ khỏi dự án:', error);
      message.error(error?.response?.data?.message || 'Không thể xóa dữ liệu khỏi dự án');
      setRefreshTrigger((prev) => prev + 1);
    }
  };

  return (
    <UI_Wrapper
    >
      <UI_Content>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <LayerThumbnail data={props.data} />
          <UI_LayerName title={props.data.title || props.data.name}>{props.data.title || props.data.name}</UI_LayerName>
        </div>
        <UI_Controller>
          {props.data.processStatus === MapData_ProcessStatus.SUCCESS && (
            <>
              <Tippy
                trigger="click"
                interactive={true}
                placement="bottom"
                offset={[0, 10]}
                delay={0}
                render={(attrs) => {
                  return (
                    <UI_Options_Wrapper>
                      {/* Phóng tới */}
                      <UI_Options_Button onClick={() => { handleFitBounds(); }}>
                        <UI_Options_ButtonIcon>
                          <PiMagnifyingGlass />
                        </UI_Options_ButtonIcon>
                        <span>Phóng tới</span>
                      </UI_Options_Button>
                      {/* End phóng tới */}

                      {/* Tuỳ chỉnh */}
                      <UI_Options_Button onClick={() => { handleEdit() }}>
                        <UI_Options_ButtonIcon>
                          <LuFilePen />
                        </UI_Options_ButtonIcon>
                        <span>Tuỳ chỉnh</span>
                      </UI_Options_Button>
                      {/* End tuỳ chỉnh */}

                      {/* Chi tiết */}
                      <UI_Options_Button onClick={() => { handleShowDetails(); }}>
                        <UI_Options_ButtonIcon>
                          <PiInfo />
                        </UI_Options_ButtonIcon>
                        <span>Chi tiết</span>
                      </UI_Options_Button>
                      {/* End chi tiết */}

                      {/* Xem bảng dữ liệu */}
                      <UI_Options_Button onClick={() => {
                        setSelected_MapData_DataTable(props.data!);
                        setShowDataTable(true);
                      }}>
                        <UI_Options_ButtonIcon>
                          <PiTable />
                        </UI_Options_ButtonIcon>
                        <span>Xem bảng dữ liệu</span>
                      </UI_Options_Button>
                      {/* End Xem bảng dữ liệu */}

                      {/* Nhân đôi */}
                      <UI_Options_Button>
                        <UI_Options_ButtonIcon>
                          <PiCopy />
                        </UI_Options_ButtonIcon>
                        <span>Nhân đôi</span>
                      </UI_Options_Button>
                      {/* End Nhân đôi */}

                      {/* Xuất */}
                      <UI_Options_Button>
                        <UI_Options_ButtonIcon>
                          <TbFileExport />
                        </UI_Options_ButtonIcon>
                        <span>Xuất</span>
                      </UI_Options_Button>
                      {/* End Xuất */}

                      {/* Xoá */}
                      <Popconfirm
                        placement="right"
                        title="Xoá lớp này khỏi dự án"
                        description="Bạn có chắc chắn muốn xoá lớp này?"
                        onConfirm={() => {
                          tippyRef.current?.hide();
                          onClick_Delete();
                        }}
                        onCancel={() => { }}
                        okText="Xoá"
                        cancelText="Huỷ"
                      >
                        <UI_Options_Button>
                          <UI_Options_ButtonIcon>
                            <PiTrash />
                          </UI_Options_ButtonIcon>

                          <span>Xóa</span>
                        </UI_Options_Button>
                      </Popconfirm>
                      {/* End Xoá */}
                    </UI_Options_Wrapper>
                  )
                }}
                onCreate={(instance) => { tippyRef.current = instance; }}
              >
                <Button type="text" style={{ padding: 8 }}><IoEllipsisHorizontal /></Button>
              </Tippy>
              <Button type="text" style={{ padding: 8 }} onClick={() => {
                if (props.data.visible) {
                  props.toggleVisibility(false);
                } else {
                  props.toggleVisibility(true);
                }
              }}>
                {props.data.visible ? <PiEye /> : <PiEyeSlash style={{ color: 'gray' }} />}
              </Button>
            </>
          )}
          {[MapData_ProcessStatus.PENDING, MapData_ProcessStatus.PROCESSING, MapData_ProcessStatus.SUBMITED].includes(props.data.processStatus as MapData_ProcessStatus) && (
            <Oval
              visible={true}
              height="22"
              width="22"
              color="#229DBD"
              secondaryColor='#229DBD'
              ariaLabel="oval-loading"
              wrapperStyle={{}}
              wrapperClass=""
              strokeWidth={8}
            />
          )}
          {[MapData_ProcessStatus.FAILED, undefined, null].includes(props.data.processStatus) && (
            <Popconfirm
              placement="left"
              title="Xoá dữ liệu lỗi này khỏi dự án"
              description="Bạn có chắc chắn muốn xoá dữ liệu này?"
              onConfirm={onClick_Delete}
              okText="Xoá"
              cancelText="Huỷ"
            >
              <Button type="text" style={{ padding: 8 }} title="Xoá khỏi dự án">
                <MdClose color="red" />
              </Button>
            </Popconfirm>
          )}
        </UI_Controller>
      </UI_Content >
    </UI_Wrapper >
  );
}

export default memo(LayerItem);

const UI_Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  padding: 6px 0px;
  border-bottom: 0.5px solid #e0e0e0;
`

const UI_Content = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 30px;
  width: 100%;
`

const UI_Controller = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`


const UI_LayerName = styled.span`
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
`

const UI_Options_Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  padding: 5px 0;
  min-width: 100px;
`

const UI_Options_Button = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: 0.3s;
  font-size: 14px;
  &:hover {
    background-color: var(--color-primary);
    color: white;
  }
  &.active {
    background-color: var(--color-primary);
    color: white;
  }
`
const UI_Options_ButtonIcon = styled.div`
  width: 24px;
`